import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { CheckCircle, MessageCircle, Mail, Phone, Home, Copy, ExternalLink, Gift, Share2 } from 'lucide-react';

const ThankYouPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [referralCopied, setReferralCopied] = useState(false);

  // Get payment details from URL params
  const telegramLink = searchParams.get('telegram_link') || '';
  const packageName = searchParams.get('package') || 'Subscription';
  const amount = searchParams.get('amount') || '0';
  const userName = searchParams.get('name') || 'Subscriber';
  const userEmail = searchParams.get('email') || '';
  const subscriptionId = searchParams.get('subscription_id') || '';
  const isPending = searchParams.get('pending') === 'true';
  const startDate = searchParams.get('start_date') || '';
  const userReferralCode = searchParams.get('referral_code') || '';
  const isAutopay = searchParams.get('autopay') === 'true';

  const whatsappNumber = "919152202340";
  const whatsappMessage = encodeURIComponent(`Hi, I just subscribed to ${packageName}. My email is ${userEmail}. Subscription ID: ${subscriptionId}`);
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  const copyTelegramLink = () => {
    navigator.clipboard.writeText(telegramLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(userReferralCode);
    setReferralCopied(true);
    setTimeout(() => setReferralCopied(false), 2000);
  };

  const shareReferral = () => {
    const shareText = `Join Upper Circuit Stocks and get amazing trading education! Use my referral code: ${userReferralCode}`;
    const shareUrl = `https://uppercircuitstocks.com/?ref=${userReferralCode}`;
    
    if (navigator.share) {
      navigator.share({
        title: 'Upper Circuit Stocks Referral',
        text: shareText,
        url: shareUrl
      });
    } else {
      navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      setReferralCopied(true);
      setTimeout(() => setReferralCopied(false), 2000);
    }
  };

  // Redirect if no subscription data
  useEffect(() => {
    if (!subscriptionId && !telegramLink) {
      // Allow 3 seconds then redirect if no data
      const timer = setTimeout(() => {
        // Don't redirect immediately, user might have refreshed
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [subscriptionId, telegramLink]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black text-white">
      {/* Header */}
      <header className="bg-black/80 backdrop-blur-sm border-b border-golden/20 py-4 px-4">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_invest-links/artifacts/kydgz8tm_WhatsApp%20Image%202025-08-23%20at%203.47.06%20PM.jpeg" 
              alt="Upper Circuit Stocks Logo" 
              className="h-12 w-auto"
            />
          </div>
          <Button 
            onClick={() => navigate('/')}
            variant="outline"
            className="border-golden text-golden hover:bg-golden hover:text-black"
          >
            <Home className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12 max-w-3xl">
        {/* Success Animation */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-green-500 rounded-full mb-6 animate-pulse">
            <CheckCircle className="w-16 h-16 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-golden mb-4">
            {isPending ? 'Payment Confirmed!' : 'Thank You!'}
          </h1>
          <p className="text-xl text-gray-300">
            {isPending 
              ? 'Your payment has been received successfully.' 
              : 'Your subscription is now active.'}
          </p>
        </div>

        {/* Subscription Details Card */}
        <Card className="bg-gray-900/80 border-golden/30 mb-8">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold text-golden mb-4 flex items-center">
              📋 Subscription Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-300">
              <div>
                <p className="text-sm text-gray-500">Subscriber Name</p>
                <p className="font-medium text-white">{userName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium text-white">{userEmail}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Package</p>
                <p className="font-medium text-golden">{packageName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Amount Paid</p>
                <p className="font-medium text-green-400">₹{parseInt(amount).toLocaleString()}</p>
              </div>
              {subscriptionId && (
                <div className="md:col-span-2">
                  <p className="text-sm text-gray-500">Subscription ID</p>
                  <p className="font-mono text-sm text-gray-400">{subscriptionId}</p>
                </div>
              )}
              {isAutopay && (
                <div className="md:col-span-2 bg-green-900/30 border border-green-500/20 rounded-lg p-3 mt-2" data-testid="autopay-confirmation">
                  <p className="text-green-400 font-medium text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" /> Auto-Renewal Enabled
                  </p>
                  <p className="text-gray-400 text-xs mt-1">Your subscription will automatically renew monthly. You can manage or cancel auto-renewal from your dashboard.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Telegram Access Card - Main CTA */}
        {!isPending && telegramLink ? (
          <Card className="bg-gradient-to-r from-blue-900/50 to-blue-800/50 border-blue-500/50 mb-8 overflow-hidden">
            <CardContent className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500 rounded-full mb-4">
                <MessageCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                Join Your Telegram Group
              </h2>
              <p className="text-blue-200 mb-6">
                Click the button below to join your exclusive trading group
              </p>
              
              {/* Main Telegram Button */}
              <a 
                href={telegramLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white text-lg font-semibold py-4 px-8 rounded-lg transition-all transform hover:scale-105 shadow-lg hover:shadow-blue-500/50 mb-4"
              >
                <MessageCircle className="w-6 h-6" />
                Click Here to Join Telegram Group
                <ExternalLink className="w-5 h-5" />
              </a>

              {/* Enable Notifications Button */}
              <div className="mt-4 pt-4 border-t border-blue-500/30">
                <p className="text-sm text-gray-400 mb-2">📱 Enable instant notifications:</p>
                <a
                  href="https://t.me/UPPERCIRCUITSTOCKS_Bot?start=subscribe"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.692-1.653-1.123-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.242-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.015 3.333-1.386 4.025-1.627 4.477-1.635.099-.002.321.023.465.141.121.1.154.234.17.331.015.098.034.322.019.496z"/>
                  </svg>
                  Enable Telegram Notifications
                </a>
                <p className="text-xs text-gray-500 mt-2">Get subscription confirmations & updates on Telegram</p>
              </div>

              {/* Copy Link Option */}
              <div className="mt-4">
                <p className="text-sm text-gray-400 mb-2">Or copy the group link:</p>
                <div className="flex items-center justify-center gap-2 bg-gray-800/50 rounded-lg p-3 max-w-md mx-auto">
                  <code className="text-sm text-blue-300 truncate flex-1">{telegramLink}</code>
                  <Button 
                    onClick={copyTelegramLink}
                    variant="ghost" 
                    size="sm"
                    className="text-blue-400 hover:text-blue-300"
                  >
                    <Copy className="w-4 h-4" />
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : isPending ? (
          <Card className="bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border-yellow-500/50 mb-8">
            <CardContent className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-500 rounded-full mb-4">
                <span className="text-3xl">⏰</span>
              </div>
              <h2 className="text-2xl font-bold text-yellow-400 mb-2">
                Access Pending
              </h2>
              <p className="text-gray-300 mb-4">
                Your subscription will start on <span className="text-yellow-400 font-semibold">{startDate}</span>
              </p>
              <p className="text-sm text-gray-400">
                You will receive your Telegram group link via email on the start date.
              </p>
            </CardContent>
          </Card>
        ) : null}

        {/* Referral Code Card - Earn Points! */}
        {userReferralCode && (
          <Card className="bg-gradient-to-r from-purple-900/40 to-pink-900/40 border-purple-500/50 mb-8">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center">
                  <Gift className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-purple-300">🎁 Earn Rewards!</h2>
                  <p className="text-sm text-gray-400">Share & earn points on every referral</p>
                </div>
              </div>
              
              <div className="bg-black/30 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-400 mb-2">Your Referral Code:</p>
                <div className="flex items-center gap-3">
                  <code className="flex-1 bg-purple-900/50 text-purple-300 px-4 py-3 rounded-lg text-lg font-mono font-bold text-center">
                    {userReferralCode}
                  </code>
                  <Button 
                    onClick={copyReferralCode}
                    variant="outline"
                    className="border-purple-500 text-purple-300 hover:bg-purple-900/50"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    {referralCopied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>
              
              <div className="bg-purple-900/20 rounded-lg p-4 mb-4">
                <h3 className="text-purple-300 font-semibold mb-2">How it works:</h3>
                <ul className="text-sm text-gray-300 space-y-1">
                  <li>• Share your code with friends</li>
                  <li>• When they subscribe, you earn <span className="text-purple-400 font-semibold">10 points per ₹100</span> they spend</li>
                  <li>• 1 Point = ₹1 - Use points for your next subscription!</li>
                </ul>
              </div>
              
              <Button 
                onClick={shareReferral}
                className="w-full bg-purple-600 hover:bg-purple-500 text-white"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share with Friends
              </Button>
            </CardContent>
          </Card>
        )}

        {/* What's Next Card */}
        <Card className="bg-gray-900/80 border-golden/30 mb-8">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold text-golden mb-4">
              📝 What&apos;s Next?
            </h2>
            <ol className="space-y-3 text-gray-300">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-golden text-black rounded-full flex items-center justify-center text-sm font-bold">1</span>
                <span>{isPending ? 'Wait for your start date' : 'Join the Telegram group using the button above'}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-golden text-black rounded-full flex items-center justify-center text-sm font-bold">2</span>
                <span>Check your email for confirmation and login credentials</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-golden text-black rounded-full flex items-center justify-center text-sm font-bold">3</span>
                <span>Enable notifications in Telegram to never miss an update</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-golden text-black rounded-full flex items-center justify-center text-sm font-bold">4</span>
                <span>Share your referral code and earn points!</span>
              </li>
            </ol>
          </CardContent>
        </Card>

        {/* Support Section */}
        <Card className="bg-gray-900/80 border-golden/30">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold text-golden mb-4">
              📞 Need Help?
            </h2>
            <p className="text-gray-400 mb-4">
              Our support team is here to help you get started.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <a 
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg transition-colors"
              >
                <Phone className="w-5 h-5" />
                Chat on WhatsApp
              </a>
              <a 
                href="mailto:chiragbrathod1988@gmail.com"
                className="flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 text-white py-3 px-4 rounded-lg transition-colors"
              >
                <Mail className="w-5 h-5" />
                Email Support
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Back to Home Button */}
        <div className="text-center mt-8">
          <Button 
            onClick={() => navigate('/')}
            className="bg-golden text-black hover:bg-golden-light font-semibold py-3 px-8"
          >
            <Home className="w-5 h-5 mr-2" />
            Back to Home
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-black/90 border-t border-golden/20 py-6 px-4 mt-12">
        <div className="container mx-auto text-center">
          <p className="text-gray-500 text-sm">
            © 2024 Upper Circuit Stocks by Chirag Rathod. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ThankYouPage;
